import clsx from 'clsx';
import { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CheckCircle,
  CloseCircleIcon,
  PhotoCameraIcon,
  UploadIcon,
} from '../../../../components/Icons';
import { Heading } from '../Heading';

interface DefaultSelfieProps {
  handleStartCamera: () => void;
  handleSetPreview: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const photoExamples = [
  {
    src: '/assets/images/good.png',
    alt: 'Good example',
    isGood: true,
  },
  {
    src: '/assets/images/bad.png',
    alt: 'Bad example 1',
    isGood: false,
  },
  {
    src: '/assets/images/bad1.png',
    alt: 'Bad example 2',
    isGood: false,
  },
];

const DefaultSelfie: React.FC<DefaultSelfieProps> = memo(
  ({ handleStartCamera, handleSetPreview }) => {
    const { t } = useTranslation();

    useEffect(() => {
      if (
        window.webkit &&
        window.webkit.messageHandlers &&
        window.webkit.messageHandlers.appBridge
      ) {
        const payload = {
          action: 'files/filter',
          source: ['image/png', 'image/jpeg'],
          allowsMultipleSelection: false,
        };
        window.webkit.messageHandlers.appBridge.postMessage(payload);
      } else {
        console.warn('Native bridge not available (not running in the app?)');
      }
    }, []);

    return (
      <div className="max-w-[600px] mx-auto mb-auto sm:mb-0">
        <Heading
          title={t('twin.selfieTitle')}
          subtitle={t('twin.selfieSubtitle')}
        />
        <p className="text-[#D5D5DB] font-normal leading-[150%] mb-4 text-center">
          {t('twin.photoInstruction')}
        </p>
        <div className="flex items-center justify-center sm:justify-between gap-4 mb-4 sm:mb-8">
          {photoExamples.map((example, index) => (
            <div
              key={index}
              className={clsx('relative', {
                'hidden sm:block': index === photoExamples.length - 1,
              })}>
              <img
                className="h-auto sm:h-[109.671px] self-stretch aspect-[71/40] rounded-2xl"
                src={example.src}
                alt={example.alt}
              />
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full flex items-center justify-center bg-white overflow-visible">
                {example.isGood ? (
                  <div className="text-green-200">
                    <CheckCircle size={24} />
                  </div>
                ) : (
                  <div className="text-red-500">
                    <CloseCircleIcon width={24} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center gap-4 flex-wrap pt-1 sm:pt-0">
          <button
            data-testid="default-selfie-take-btn"
            className="btn btn-primary sm:!grow-0 !grow"
            onClick={handleStartCamera}>
            <PhotoCameraIcon size={16} />
            <span className="ml-2">{t('twin.takeSelfie')}</span>
          </button>
          <label
            data-testid="default-selfie-upload-btn"
            className="btn btn-primary sm:!grow-0 !grow"
            htmlFor="img-field">
            <input
              type="file"
              accept="image/*"
              id="img-field"
              className="pointer-events-none opacity-0 hidden"
              onChange={handleSetPreview}
            />
            <UploadIcon size={16} />
            <span className="ml-2">{t('twin.uploadPhoto')}</span>
          </label>
        </div>
        <div className="mt-2 text-white/50 text-center text-xs font-normal leading-[150%]">
          {t('twin.replacePhotoNote')}
        </div>
      </div>
    );
  },
);

export { DefaultSelfie };
